import React, { useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Eye, Pencil } from 'lucide-react';
import styled from 'styled-components';
import { Button, Message } from '../admin/ui';
import PlaceholderEditor, { KIND_STYLE } from './PlaceholderEditor';
import { placeholderGroupLabel } from './emailAdminModel';

// The pieces of the e-mail editor that more than one screen uses: the saved template on the E-Mails
// screen (EmailTemplateEditor) and a one-time edit before a send (OneTimeEmailDialog, and the
// program screen's "tell the affected people" step). The owner's layout rule holds for all of them:
// the editor and the preview are two views of one column, never columns beside each other.
//
// The text is {{placeholder}} HTML; the preview is always the backend's rendering, never a second
// renderer here. Everything is controlled: the owning component holds subject/html and the answer.
export const PREVIEW_DELAY_MS = 500;

// What the backend said, as a message for the admin: a plain-text body wins, then a message field.
export const backendMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  if (data && typeof data.error === 'string' && data.error) return data.error;
  return err?.message || fallback;
};

const ViewSwitch = styled.div`
  display: flex;
  gap: .35rem;
  margin: 1rem 0;
`;

const ViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  border: 1px solid ${(p) => (p.$active ? '#47ABDB' : '#cbd5e1')};
  background: ${(p) => (p.$active ? '#eff9fe' : '#fff')};
  color: ${(p) => (p.$active ? '#111D4E' : '#475569')};
  padding: .45rem .8rem;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
`;

const PlaceholderButton = styled.button`
  display: flex;
  gap: .6rem;
  align-items: baseline;
  width: 100%;
  text-align: left;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: .4rem .55rem;
  cursor: pointer;
  font: inherit;

  &:hover {
    border-color: #94a3b8;
  }
`;

export const Legend = () => (
  <div style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap', fontSize: '.74rem', color: '#475569' }}>
    {[
      ['TEXT', <FormattedMessage key="t" id="emailAdmin.template.legend.text" defaultMessage="value from the database" />],
      ['BLOCK', <FormattedMessage key="b" id="emailAdmin.template.legend.block" defaultMessage="ready-made block (buttons, images, details)" />],
      ['UNKNOWN', <FormattedMessage key="u" id="emailAdmin.template.legend.unknown" defaultMessage="unknown placeholder" />],
    ].map(([kind, label]) => (
      <span key={kind} style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem' }}>
        <span style={{ width: 14, height: 10, borderRadius: 2, display: 'inline-block', ...KIND_STYLE[kind] }} />{label}
      </span>
    ))}
  </div>
);

// The Edit | Preview switch, one column each.
export const EmailViewSwitch = ({ view, onChange, label }) => {
  const intl = useIntl();
  const aria = label || intl.formatMessage({ id: 'emailAdmin.template.viewLabel', defaultMessage: 'Editor or preview' });
  return (
    <ViewSwitch role="group" aria-label={aria}>
      <ViewButton type="button" $active={view === 'edit'} aria-pressed={view === 'edit'} onClick={() => onChange('edit')}>
        <Pencil size={15} /><FormattedMessage id="emailAdmin.template.viewEdit" defaultMessage="Edit" />
      </ViewButton>
      <ViewButton type="button" $active={view === 'preview'} aria-pressed={view === 'preview'} onClick={() => onChange('preview')}>
        <Eye size={15} /><FormattedMessage id="emailAdmin.template.viewPreview" defaultMessage="Preview" />
      </ViewButton>
    </ViewSwitch>
  );
};

// Subject, body, the colour legend and the placeholder list (a click inserts the token where the
// cursor last was). Owns only the focus/insert state; every keystroke goes back through the props.
export const TemplateFields = ({ subject, html, onChangeSubject, onChangeHtml, placeholders = [] }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const lastFocused = useRef('html');

  const kindOf = useCallback((key) => placeholders.find((p) => p.key === key)?.kind, [placeholders]);
  const groups = [];
  for (const p of placeholders) {
    let group = groups.find(([name]) => name === p.group);
    if (!group) { group = [p.group, []]; groups.push(group); }
    group[1].push(p);
  }

  const insert = (key) => {
    const token = `{{${key}}}`;
    const target = lastFocused.current === 'subject' ? subjectRef.current : htmlRef.current;
    const value = lastFocused.current === 'subject' ? subject : html;
    const set = lastFocused.current === 'subject' ? onChangeSubject : onChangeHtml;
    const start = target?.selectionStart ?? value.length;
    const end = target?.selectionEnd ?? value.length;
    set(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      if (!target) return;
      target.focus();
      target.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '.82rem', fontWeight: 600, color: '#334155', marginBottom: '.35rem' }}>
        <FormattedMessage id="emailAdmin.template.subject" defaultMessage="Subject" />
      </label>
      <PlaceholderEditor ref={subjectRef} singleLine value={subject} onChange={onChangeSubject} kindOf={kindOf}
        onFocus={() => { lastFocused.current = 'subject'; }} ariaLabel={t('emailAdmin.template.subject', 'Subject')} />
      <label style={{ display: 'block', fontSize: '.82rem', fontWeight: 600, color: '#334155', margin: '1rem 0 .35rem' }}>
        <FormattedMessage id="emailAdmin.template.body" defaultMessage="Email (HTML)" />
      </label>
      <PlaceholderEditor ref={htmlRef} value={html} onChange={onChangeHtml} kindOf={kindOf} rows={16}
        onFocus={() => { lastFocused.current = 'html'; }} ariaLabel={t('emailAdmin.template.body', 'Email (HTML)')} />
      <div style={{ marginTop: '.5rem' }}><Legend /></div>

      {placeholders.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <div style={{ fontSize: '.82rem', fontWeight: 700, color: '#334155', marginBottom: '.5rem' }}>
            <FormattedMessage id="emailAdmin.template.placeholders" defaultMessage="Placeholders" />
          </div>
          {groups.map(([group, items]) => (
            <div key={group} style={{ marginBottom: '.8rem' }}>
              <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b', fontWeight: 700, marginBottom: '.35rem' }}>
                {placeholderGroupLabel(t, group)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                {items.map((p) => (
                  <PlaceholderButton key={p.key} type="button" onClick={() => insert(p.key)}
                    title={t('emailAdmin.template.insert', 'Insert {token}', { token: `{{${p.key}}}` })}>
                    <code style={{ flex: 'none', fontSize: '.75rem', padding: '0 .3rem', ...KIND_STYLE[p.kind] }}>{`{{${p.key}}}`}</code>
                    <span style={{ fontSize: '.78rem', color: '#334155', lineHeight: 1.4 }}>
                      <strong>{p.label}</strong> — {p.description}
                      <span style={{ display: 'block', color: '#94a3b8', fontSize: '.72rem' }}>
                        <FormattedMessage id="emailAdmin.template.source" defaultMessage="Comes from: {source}" values={{ source: p.source }} />
                      </span>
                    </span>
                  </PlaceholderButton>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// The backend's rendering: subject bar and a sandboxed iframe (no scripts, no links to click
// through). `loading` dims it while a newer render is on its way. The subject is shown once, in the
// bar above the email; the line over it only says when a newer render is on its way.
export const EmailPreview = ({ subject, html, loading = false, title, refresh }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  return (
    <div>
      {(loading || refresh) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
          <span style={{ fontSize: '.78rem', color: '#64748b' }}>
            {loading && <FormattedMessage id="emailAdmin.template.previewUpdating" defaultMessage="Updating…" />}
          </span>
          {refresh && (
            <Button type="button" secondary disabled={loading} onClick={refresh}>
              <Eye size={15} /><FormattedMessage id="emailAdmin.template.previewRefresh" defaultMessage="Refresh preview" />
            </Button>
          )}
        </div>
      )}
      <div style={{ fontSize: '.82rem', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderBottom: 0, padding: '.45rem .6rem', overflowWrap: 'anywhere' }}>
        <strong><FormattedMessage id="emailAdmin.template.previewSubjectLabel" defaultMessage="Subject" />:</strong> {subject || '…'}
      </div>
      <iframe title={title || t('emailAdmin.template.previewTitle', 'Preview of the email')} sandbox=""
        srcDoc={html ? `<base target="_blank">${html}` : ''}
        style={{ width: '100%', height: '60vh', minHeight: 240, border: '1px solid #e2e8f0', background: '#fff',
          opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }} />
    </div>
  );
};

// The problems a save or send would report, verbatim, in the red box the editor uses.
export const TemplateProblems = ({ problems = [], titleId, titleDefault }) => (
  problems.length > 0 ? (
    <Message error role="alert" style={{ marginBottom: '.75rem' }}>
      <strong><FormattedMessage id={titleId} defaultMessage={titleDefault} /></strong>
      <ul style={{ margin: '.3rem 0 0', paddingLeft: '1.1rem' }}>
        {problems.map((p) => <li key={p}>{p}</li>)}
      </ul>
    </Message>
  ) : null
);
